import React, { useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { API_URL } from "../../../../config";
import { getHomeRouteByRole } from "../../../../utils/rutas";
import { COURSE_ADMIN_ASSIGN_ROLES, STAFF_ROLES, isAlumno } from "../../../../utils/roles";
import {
  normalizeCourseContenido,
  buildContenidoPayloadForSave,
  ensureContenidoKeysForPestanas,
  applyCourseTabNameInput,
  applyTabRenameMapToTestsArray,
  buildTabRenameMap,
  findTemarioContentKey,
  getNamedCourseTabs,
  getCourseTabsSaveBlocker,
  hasDuplicateCourseTabs,
  normalizeCourseTabName,
  resizeUniqueTabKeys,
  resolvePublishedCourseTabsAndContenido,
} from "../../../../utils/normalizeCourseShape";
import { resolveSelectedCourseTab } from "../../../../utils/resolveSelectedCourseTab";
import { listPathOptions } from "../../../../utils/temarioTree";
import "./CrearCurso.css";
import "./VistaCursosAdmin.css";
import { TEMARIO_DEPTH_LABELS } from "./editarCursoConstants";
import { buildCourseTestsPayloadObject } from "./editarCursoServerFormat";
import { groupCourseTestsByTab } from "./groupCourseTestsByTab";
import { uploadCourseFileWithProgress } from "./uploadCourseFileWithProgress";
import ViewportSelect from "../../../../components/ViewportSelect";
import EditarCursoTestsTab from "./EditarCursoTestsTab";
import CourseDescriptionEditor from "../../../../components/CourseDescriptionEditor";
import ResponsiveActionSidebar from "../../../../components/ResponsiveActionSidebar";
import EditarCursoDocsTab from "./EditarCursoDocsTab";
import EditarCursoVistaTab from "./EditarCursoVistaTab";
import EditarCursoDescuentosTab from "./EditarCursoDescuentosTab";
import EditarCursoTestimoniosTab from "./EditarCursoTestimoniosTab";
import { docAddFailureMessage, getDocAddValidationMessage, mergeArchivoIntoBloqueLista } from "./editarCursoDocumentAdd";
import { mapSearchParamToEditarCursoTab } from "../../../../utils/editarCursoTabQuery";
import { extractMongoObjectIdHex } from "../../../../utils/helpers";
import { formatCourseEditSaveFeedback } from "../../../../utils/formatCourseEditSaveFeedback";
import { normalizeCourseDurationInput } from "../../../../utils/formatCourseDurationLabel";
import {
  buildCourseEditSnapshotForSave,
  hasUnsavedCourseEdits,
  listCamposTocadosChanged,
  previousTabsFromCourseBaseline,
} from "./editarCursoCamposTocados";
import { getMessage } from "../../../../data/locales";
import CourseTabCountInput from "../../../../components/CourseTabCountInput";
import {
  COURSE_CLOSE_DATE_MAX,
  COURSE_CLOSE_DATE_MIN,
  getCourseCloseDateError,
} from "../../../../utils/courseCloseDate";
import { confirmUnsavedNavigation } from "../../../../utils/confirmUnsavedNavigation";
import { useBeforeUnloadWhen } from "../../../../hooks/useBeforeUnloadWhen";
import { getEditCourseSidebarTabs } from "../../../../utils/courseEditorSidebarLabels";
import { createTabStableIds, parseTestsArrayFromApi } from "./editarCursoLoadTransforms";
import { parseCourseTagsInput } from "../../../../utils/parseCourseTagsInput";

const SUCCESS_MESSAGE_AUTO_HIDE_MS = 4500;

function adminIdKey(raw) {
  return extractMongoObjectIdHex(raw) || (typeof raw === "string" ? raw.trim() : String(raw || ""));
}

function snapshotFromPopulatedAdmin(a) {
  if (!a || typeof a !== "object") return null;
  const nombre = [a.nombre, a.primerApellido, a.segundoApellido].filter((x) => typeof x === "string" && x.trim()).join(" ").trim();
  return {
    nombre: nombre || "",
    email: typeof a.email === "string" ? a.email.trim() : "",
    rol: typeof a.rol === "string" ? a.rol.trim() : "",
  };
}

function filterAdminIdsExcludingAlumnos(ids, staff) {
  return ids.filter((id) => {
    const key = adminIdKey(id);
    const u = staff.find((x) => adminIdKey(x._id) === key);
    if (!u) {
      return false;
    }
    return !isAlumno(u.rol);
  });
}

function adminIdAllowedByResolvedLabel(key, labelsById) {
  if (!key) return false;
  const snap = labelsById[key];
  if (!snap || typeof snap.rol !== "string" || snap.rol.trim() === "") {
    return false;
  }
  return !isAlumno(snap.rol);
}

function resolveAdministradoresForSave(ids, staff, staffLoaded, labelsById) {
  if (!staffLoaded) {
    return ids.filter((id) => adminIdAllowedByResolvedLabel(adminIdKey(id), labelsById));
  }
  if (staff.length > 0) {
    return filterAdminIdsExcludingAlumnos(ids, staff);
  }
  return ids.filter((id) => adminIdAllowedByResolvedLabel(adminIdKey(id), labelsById));
}

function formatAdminListLabel(snapshot, staffUser, uidFallback) {
  if (staffUser) {
    const nombre = [staffUser.nombre, staffUser.primerApellido]
      .filter((x) => typeof x === "string" && x.trim())
      .join(" ")
      .trim();
    const email = typeof staffUser.email === "string" ? staffUser.email.trim() : "";
    const role = typeof staffUser.rol === "string" ? staffUser.rol.trim() : "";
    const parts = [nombre || null, role || null, email || null].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : uidFallback;
  }
  if (snapshot && (snapshot.nombre || snapshot.email || snapshot.rol)) {
    const parts = [snapshot.nombre || null, snapshot.rol || null, snapshot.email || null].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : uidFallback;
  }
  return uidFallback && uidFallback.length > 12 ? `Usuario (…${uidFallback.slice(-8)})` : `Usuario (${uidFallback || "?"})`;
}

const EditarCurso = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const postCreateNoticeRef = useRef(false);
  useEffect(() => {
    postCreateNoticeRef.current = false;
  }, [id]);

  const [archivoImagen, setArchivoImagen] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!archivoImagen) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(archivoImagen);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [archivoImagen]);

  const [cargando, setCargando] = useState(true);
  const [mensaje, setMensaje] = useState("");
  const mensajeRef = useRef(null);

  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [duracion, setDuracion] = useState("");
  const [precio, setPrecio] = useState("");
  const [descuento, setDescuento] = useState("");
  const [codigoDescuento, setCodigoDescuento] = useState("");
  const [precioFinal, setPrecioFinal] = useState("");
  const [imagen, setImagen] = useState("");
  const [esAbierto, setEsAbierto] = useState(false);
  const [fechaCierre, setFechaCierre] = useState("");
  const [contenidoDescargable, setContenidoDescargable] = useState(false);
  const [puedePublicar, setPuedePublicar] = useState(false);
  const [publicadoAlumnos, setPublicadoAlumnos] = useState(false);
  const [disponibleCompra, setDisponibleCompra] = useState(true);
  const [tagsInput, setTagsInput] = useState("");
  const [cambiosPendientesPublicacion, setCambiosPendientesPublicacion] = useState(false);
  const [catalogStateLoaded, setCatalogStateLoaded] = useState(false);
  const courseCompareBaselineRef = useRef(null);
  const baselineSeedKeyRef = useRef("");
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [estadoCatalogoMsg, setEstadoCatalogoMsg] = useState("");
  const currentCatalogCourseIdRef = useRef(id);

  useEffect(() => {
    currentCatalogCourseIdRef.current = id;
    setEstadoCatalogoMsg("");
    setCatalogBusy(false);
  }, [id]);
  const tabParam = searchParams.get("tab");
  const initialEditTab = mapSearchParamToEditarCursoTab(tabParam);
  const [activeTab, setActiveTab] = useState(initialEditTab);
  const tabParamSyncRef = useRef(tabParam);
  useEffect(() => {
    const prev = tabParamSyncRef.current;
    tabParamSyncRef.current = tabParam;

    const isEmptyTabParam = (v) => v == null || String(v).trim() === "";
    if (isEmptyTabParam(tabParam)) {
      if (!isEmptyTabParam(prev)) {
        setActiveTab("datos");
      }
      return;
    }
    const mapped = mapSearchParamToEditarCursoTab(tabParam);
    setActiveTab((prevTab) => (prevTab === mapped ? prevTab : mapped));
  }, [tabParam]);
  const [actionBarVisible, setActionBarVisible] = useState(true);
  useEffect(() => {
    try {
      localStorage.setItem("editarCurso_actionBarVisible", JSON.stringify(actionBarVisible));
    } catch (e) {
      console.warn("editarCurso_actionBarVisible write", e);
    }
  }, [actionBarVisible]);
  const [pestanasCurso, setPestanasCurso] = useState([]);
  const lastUniqueTabKeysRef = useRef([]);
  const [bloquesContenido, setBloquesContenido] = useState({});
  useEffect(() => {
    setBloquesContenido((prev) => {
      let changed = false;
      const next = { ...prev };
      pestanasCurso.forEach((p) => {
        const k = (p ?? "").trim();
        if (!k) {
          return;
        }
        if (!Array.isArray(next[k])) {
          next[k] = [];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [pestanasCurso]);
  const [tests, setTests] = useState([]);

  const testsPorBloque = useMemo(() => groupCourseTestsByTab(tests, pestanasCurso), [tests, pestanasCurso]);

  const [bloqueSeleccionado, setBloqueSeleccionado] = useState("");
  useEffect(() => {
    setBloqueSeleccionado((current) =>
      resolveSelectedCourseTab(current, pestanasCurso, bloquesContenido),
    );
  }, [pestanasCurso, bloquesContenido]);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [tipoInsert, setTipoInsert] = useState("nuevo");
  const [subseccion, setSubseccion] = useState("");
  const [rutaDestinoKey, setRutaDestinoKey] = useState("");
  const [rutaPadreKey, setRutaPadreKey] = useState("");
  const [posicion, setPosicion] = useState("debajo");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [lastUploadError, setLastUploadError] = useState("");
  const [rol, setRol] = useState("");
  const [rolReady, setRolReady] = useState(false);
  const [puedeModificar, setPuedeModificar] = useState(false);
  const [adminIds, setAdminIds] = useState([]);
  const [adminLabelsById, setAdminLabelsById] = useState(() => ({}));
  const [usuariosStaff, setUsuariosStaff] = useState([]);
  const [usuariosStaffLoaded, setUsuariosStaffLoaded] = useState(false);
  const [addAdminId, setAddAdminId] = useState("");
  const [savingAdmins, setSavingAdmins] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const loadRol = async () => {
      try {
        const r = await fetch(`${API_URL}/verificar-token`, {
          credentials: "include",
        });
        if (cancelled) return;
        if (!r.ok) {
          setMensaje((prev) =>
            prev || "⚠️ No se pudo verificar la sesión. Recarga la página o vuelve a iniciar sesión.",
          );
          return;
        }
        const d = await r.json();
        if (!cancelled) {
          setRol(d?.usuario?.rol || "");
          setPuedePublicar(d?.usuario?.puedePublicar === true);
          setPuedeModificar(d?.usuario?.permisos?.puedeModificar === true);
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Error verificando token:", e);
          setMensaje("⚠️ Error verificando permisos. Recarga la página.");
        }
      } finally {
        if (!cancelled) {
          setRolReady(true);
        }
      }
    };
    void loadRol();
    return () => {
      cancelled = true;
    };
  }, []);
  const canEditByRoleOrPermission =
    STAFF_ROLES.includes(rol) || puedeModificar === true;
  const canEditPrice = COURSE_ADMIN_ASSIGN_ROLES.includes(rol);
  const canEditStatus = COURSE_ADMIN_ASSIGN_ROLES.includes(rol);
  const canManageTabs = rolReady && canEditByRoleOrPermission;
  const canManageContent = rolReady && canEditByRoleOrPermission;
  const canManageTests = rolReady && canEditByRoleOrPermission;
  const canAssignCourseAdmins = COURSE_ADMIN_ASSIGN_ROLES.includes(rol);
  useEffect(() => {
    if (!rolReady) return;
    if (!canEditByRoleOrPermission) {
      navigate(getHomeRouteByRole(rol || "alumno"), { replace: true });
    }
  }, [rolReady, canEditByRoleOrPermission, rol, navigate]);

  useEffect(() => {
    if (postCreateNoticeRef.current) {
      return;
    }
    const st = location.state;
    if (st && typeof st === "object" && st.avisoCursoCreado === true) {
      postCreateNoticeRef.current = true;
      setMensaje("✅ Curso creado.");
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!mensaje || !mensaje.trim()) {
      return undefined;
    }

    const frameId = requestAnimationFrame(() => {
      mensajeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const isSuccess = mensaje.trim().startsWith("✅");
    const timeoutId = isSuccess
      ? setTimeout(() => {
          setMensaje((current) => (current === mensaje ? "" : current));
        }, SUCCESS_MESSAGE_AUTO_HIDE_MS)
      : null;

    return () => {
      cancelAnimationFrame(frameId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [mensaje]);

  const currentUploadRef = useRef(null);
  const lastAttemptedFileRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    const uploadRef = currentUploadRef;
    return () => {
      mountedRef.current = false;
      const xhr = uploadRef.current;
      if (xhr && xhr.readyState !== 4) {
        try {
          xhr.abort();
        } catch (e) {
          console.warn("XHR abort on unmount", e);
        }
      }
    };
  }, []);

  const [tabStableIds, setTabStableIds] = useState([]);

  const [testActual, setTestActual] = useState({
    titulo: "",
    tipo: "temario",
    tiempo: 0,
    preguntas: [{ pregunta: "", respuestas: ["", "", "", ""], correcta: null, explicacion: "" }],
    pestanas: [],
  });
  const [preguntas, setPreguntas] = useState(testActual.preguntas);
  const [preguntaSeleccionada, setPreguntaSeleccionada] = useState(0);

  useEffect(() => {
    if (preguntaSeleccionada >= preguntas.length) {
      setPreguntaSeleccionada(Math.max(0, preguntas.length - 1));
    }
  }, [preguntas, preguntaSeleccionada]);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [indexEdicion, setIndexEdicion] = useState(undefined);

  useEffect(() => {
    const controller = new AbortController();
    setCargando(true);
    setCatalogStateLoaded(false);
    const f = async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/cursos/${id}`, {
          credentials: "include",
          signal: controller.signal,
        });
        const rawText = await r.text();
        if (!r.ok) {
          console.error("Error cargando curso:", r.status, rawText);
          if (r.status === 403) {
            let errMsg = "No tienes permiso para ver o editar este curso.";
            try {
              const errObj = JSON.parse(rawText);
              if (typeof errObj?.error === "string" && errObj.error.trim()) {
                errMsg = errObj.error.trim();
              }
            } catch {
              /* keep default */
            }
            setMensaje(errMsg);
            navigate("/GestionCursos", { replace: true, state: { gcNotice: errMsg } });
            return;
          }
          setMensaje(r.status === 404 ? "Curso no encontrado." : "Error al cargar el curso.");
          return;
        }
        let data;
        try {
          data = JSON.parse(rawText);
        } catch (parseErr) {
          console.error(parseErr);
          setMensaje("Error al cargar el curso.");
          return;
        }

        setTitulo(data.titulo || "");
        setDescripcion(data.descripcion || "");
        setDuracion(data.duracion || "");
        setPrecio(data.precio ?? "");
        setDescuento(data.descuento ?? "");
        setCodigoDescuento(data.codigoDescuento || "");
        setPrecioFinal(data.precioFinal ?? "");
        setImagen(data.imagen || "");
        setEsAbierto(data.esAbierto || false);
        setContenidoDescargable(data.contenidoDescargable === true);
        setPublicadoAlumnos(data.publicadoAlumnos !== false);
        setDisponibleCompra(data.disponibleCompra !== false);
        setTagsInput(Array.isArray(data.tags) ? data.tags.join(", ") : "");
        setCambiosPendientesPublicacion(data.cambiosPendientesPublicacion === true);
        const fcRaw = data.fechaCierre ? new Date(data.fechaCierre) : null;
        setFechaCierre(fcRaw && !Number.isNaN(fcRaw.getTime()) ? fcRaw.toISOString().slice(0, 10) : "");
        const pestanas = Array.isArray(data.pestanasCurso)
          ? data.pestanasCurso.filter((p) => typeof p === "string").map((p) => p.trim()).filter(Boolean)
          : [];
        const contenidoSrc = data.contenido || {};
        const published = resolvePublishedCourseTabsAndContenido(
          pestanas,
          normalizeCourseContenido(contenidoSrc),
        );
        const contenido = ensureContenidoKeysForPestanas(
          published.contenido,
          published.pestanasCurso,
        );
        setPestanasCurso(published.pestanasCurso);
        lastUniqueTabKeysRef.current = published.pestanasCurso.map((tab) => normalizeCourseTabName(tab));
        setTabStableIds(createTabStableIds(pestanas.length));
        setBloquesContenido(contenido);
        setTests(parseTestsArrayFromApi(data.tests));

        const adm = data.administradores || [];
        const admIds = adm.map((a) => {
          if (a && typeof a === "object" && a._id != null) {
            return adminIdKey(a._id);
          }
          return adminIdKey(a);
        });
        const labels = {};
        adm.forEach((a) => {
          if (!a || typeof a !== "object" || a._id == null) return;
          const key = adminIdKey(a._id);
          if (!key) return;
          const snap = snapshotFromPopulatedAdmin(a);
          if (snap) labels[key] = snap;
        });
        setAdminLabelsById(labels);
        setAdminIds(admIds);
        setCatalogStateLoaded(true);

      } catch (e) {
        if (e.name === "AbortError") return;
        console.error(e);
        setMensaje("Error al cargar el curso.");
      } finally {
        if (!controller.signal.aborted) {
          setCargando(false);
        }
      }
    };
    f();
    return () => controller.abort();
  }, [id, navigate]);

  const refreshCourseCompareBaseline = useCallback((overrides) => {
    const o = overrides && typeof overrides === "object" ? overrides : {};
    const imagenForBaseline = typeof o.nextImagen === "string" ? o.nextImagen : imagen;
    const adminIdsForBaseline = Array.isArray(o.nextAdminIds) ? o.nextAdminIds : adminIds;
    const tagsForBaseline = typeof o.nextTags === "string" ? o.nextTags : tagsInput;
    const disponibleCompraForBaseline =
      typeof o.nextDisponibleCompra === "boolean" ? o.nextDisponibleCompra : disponibleCompra;
    courseCompareBaselineRef.current = buildCourseEditSnapshotForSave({
      titulo,
      descripcion,
      duracion,
      imagen: imagenForBaseline,
      precio,
      descuento,
      codigoDescuento,
      precioFinal,
      esAbierto,
      fechaCierre,
      contenidoDescargable,
      pestanasCurso,
      tests,
      bloquesContenido,
      adminIds: adminIdsForBaseline,
      tagsInput: tagsForBaseline,
      disponibleCompra: disponibleCompraForBaseline,
      rol,
      adminIdKeyFn: adminIdKey,
    });
  }, [
    titulo,
    descripcion,
    duracion,
    imagen,
    precio,
    descuento,
    codigoDescuento,
    precioFinal,
    esAbierto,
    fechaCierre,
    contenidoDescargable,
    pestanasCurso,
    tests,
    bloquesContenido,
    adminIds,
    tagsInput,
    disponibleCompra,
    rol,
  ]);

  useEffect(() => {
    baselineSeedKeyRef.current = "";
  }, [id]);

  useEffect(() => {
    if (!catalogStateLoaded) {
      baselineSeedKeyRef.current = "";
    }
  }, [catalogStateLoaded]);

  useLayoutEffect(() => {
    if (!catalogStateLoaded || !rolReady || !id) {
      return;
    }
    const key = `${id}|${rol}|${String(catalogStateLoaded)}`;
    if (baselineSeedKeyRef.current === key) {
      return;
    }
    baselineSeedKeyRef.current = key;
    refreshCourseCompareBaseline();
  }, [
    catalogStateLoaded,
    rolReady,
    id,
    rol,
    titulo,
    descripcion,
    duracion,
    imagen,
    precio,
    descuento,
    codigoDescuento,
    precioFinal,
    esAbierto,
    fechaCierre,
    contenidoDescargable,
    tagsInput,
    disponibleCompra,
    pestanasCurso,
    tests,
    bloquesContenido,
    adminIds,
    refreshCourseCompareBaseline,
  ]);

  const buildCurrentEditSnapshot = useCallback(
    () =>
      buildCourseEditSnapshotForSave({
        titulo,
        descripcion,
        duracion,
        imagen,
        precio,
        descuento,
        codigoDescuento,
        precioFinal,
        esAbierto,
        fechaCierre,
        contenidoDescargable,
        pestanasCurso,
        tests,
        bloquesContenido,
        adminIds,
        tagsInput,
        disponibleCompra,
        rol,
        adminIdKeyFn: adminIdKey,
      }),
    [
      titulo,
      descripcion,
      duracion,
      imagen,
      precio,
      descuento,
      codigoDescuento,
      precioFinal,
      esAbierto,
      fechaCierre,
      contenidoDescargable,
      pestanasCurso,
      tests,
      bloquesContenido,
      adminIds,
      tagsInput,
      disponibleCompra,
      rol,
    ],
  );

  const editorHasUnsavedChanges = useCallback(() => {
    if (archivoImagen !== null) {
      return true;
    }
    return hasUnsavedCourseEdits(courseCompareBaselineRef.current, buildCurrentEditSnapshot());
  }, [archivoImagen, buildCurrentEditSnapshot]);

  useBeforeUnloadWhen(editorHasUnsavedChanges());

  const confirmLeaveCourseEditor = useCallback(() => {
    if (!editorHasUnsavedChanges()) {
      return true;
    }
    return confirmUnsavedNavigation(getMessage("courseEdit.unsavedLeaveConfirm"));
  }, [editorHasUnsavedChanges]);

  const requestCourseEditorAction = useCallback(
    (action) => {
      if (!confirmLeaveCourseEditor()) {
        return;
      }
      action();
    },
    [confirmLeaveCourseEditor],
  );

  const handleSidebarTabChange = useCallback(
    (nextTab) => {
      if (nextTab === activeTab) {
        return;
      }
      setActiveTab(nextTab);
    },
    [activeTab],
  );

  const editCourseSidebarTabs = useMemo(() => getEditCourseSidebarTabs(), []);

  useEffect(() => {
    if (!canAssignCourseAdmins || !id) return;
    let cancelled = false;
    setUsuariosStaffLoaded(false);
    (async () => {
      try {
        const r = await fetch(`${API_URL}/usuarios`, { credentials: "include" });
        if (!r.ok) {
          if (!cancelled) {
            setUsuariosStaff([]);
            setUsuariosStaffLoaded(true);
          }
          return;
        }
        const list = await r.json();
        const arr = Array.isArray(list) ? list : [];
        const staff = arr.filter((u) => u && u.rol && !isAlumno(u.rol));
        if (!cancelled) {
          setUsuariosStaff(staff);
          setUsuariosStaffLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setUsuariosStaff([]);
          setUsuariosStaffLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAssignCourseAdmins, id]);

  useEffect(() => {
    if (usuariosStaff.length === 0 || adminIds.length === 0) return;
    setAdminLabelsById((prev) => {
      const next = { ...prev };
      let changed = false;
      adminIds.forEach((uid) => {
        const key = adminIdKey(uid);
        if (!key) return;
        const hasLabel = next[key]?.nombre || next[key]?.email;
        if (hasLabel) return;
        const u = usuariosStaff.find((x) => adminIdKey(x._id) === key);
        const snap = u ? snapshotFromPopulatedAdmin(u) : null;
        if (snap && (snap.nombre || snap.email)) {
          next[key] = snap;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [usuariosStaff, adminIds]);

  const guardarAdministradoresCurso = async () => {
    if (!canAssignCourseAdmins || !id) return;
    setSavingAdmins(true);
    setMensaje("");
    try {
      const r = await fetch(`${API_URL}/api/admin/cursos/${id}/administradores`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          administradores: resolveAdministradoresForSave(
            adminIds,
            usuariosStaff,
            usuariosStaffLoaded,
            adminLabelsById,
          ),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data.error || `Error ${r.status}`);
      }
      setMensaje("✅ Administradores del curso actualizados.");
      if (data.curso?.administradores) {
        const adm = data.curso.administradores;
        const nextIds = adm.map((a) =>
          a && typeof a === "object" && a._id != null ? adminIdKey(a._id) : adminIdKey(a)
        );
        setAdminIds(nextIds);
        const labels = {};
        adm.forEach((a) => {
          if (!a || typeof a !== "object" || a._id == null) return;
          const key = adminIdKey(a._id);
          if (!key) return;
          const snap = snapshotFromPopulatedAdmin(a);
          if (snap) labels[key] = snap;
        });
        setAdminLabelsById((prev) => ({ ...prev, ...labels }));
        refreshCourseCompareBaseline({ nextAdminIds: nextIds });
      }
    } catch (e) {
      setMensaje(`❌ ${e.message || "No se pudo guardar administradores"}`);
    } finally {
      setSavingAdmins(false);
    }
  };

  // Recalcular precioFinal localmente
  useEffect(() => {
    const p = Number.parseFloat(precio);
    const d = Number.parseFloat(descuento);
    const codeOk = typeof codigoDescuento === "string" && codigoDescuento.trim() !== "";
    if (!Number.isNaN(p) && !Number.isNaN(d) && codeOk) {
      const pf = p - (p * d) / 100;
      setPrecioFinal(Number.isFinite(pf) ? pf.toFixed(2) : "");
    } else if (!Number.isNaN(p) && Number.isFinite(p)) {
      setPrecioFinal(p.toFixed(2));
    } else {
      setPrecioFinal("");
    }
  }, [precio, descuento, codigoDescuento]);

  useEffect(() => {
    if (tipoInsert !== "existente") {
      return;
    }
    const list = bloquesContenido[bloqueSeleccionado] || [];
    const opts = listPathOptions(list, TEMARIO_DEPTH_LABELS);
    const valid = new Set(opts.map((o) => o.pathKey));
    setRutaDestinoKey((cur) => (cur && valid.has(cur) ? cur : ""));
  }, [bloqueSeleccionado, bloquesContenido, tipoInsert]);

  useEffect(() => {
    if (tipoInsert !== "nuevo") {
      return;
    }
    const list = bloquesContenido[bloqueSeleccionado] || [];
    const opts = listPathOptions(list, TEMARIO_DEPTH_LABELS);
    const validParents = new Set(["", ...opts.map((o) => o.pathKey)]);
    setRutaPadreKey((cur) => (validParents.has(cur) ? cur : ""));
  }, [bloqueSeleccionado, bloquesContenido, tipoInsert]);

  const subirArchivoConProgreso = useCallback(
    (file) =>
      uploadCourseFileWithProgress(file, API_URL, {
        mountedRef,
        currentUploadRef,
        lastAttemptedFileRef,
        setUploadProgress,
        setUploadStatus,
        setLastUploadError,
        setMensaje,
      }),
    [],
  );

  const handleAddDoc = async (overrideFile) => {
    const selectedFile = overrideFile ?? archivoSeleccionado;
    const listaActual = bloquesContenido[bloqueSeleccionado] || [];
    const subseccionTrim = subseccion.trim();
    const validationMsg = getDocAddValidationMessage({
      selectedFile,
      bloqueSeleccionado,
      tipoInsert,
      rutaDestinoKey,
      listaActual,
      subseccionTrim,
    });
    if (validationMsg) {
      return setMensaje(validationMsg);
    }
    const file = selectedFile;
    const fileName = file.name || "";
    const fileType = file.type || "";

    try {
      const data = await subirArchivoConProgreso(file);
      const storageNombre = data && typeof data.nombre === "string" && data.nombre.length > 0 ? data.nombre : fileName;
      const urlPart = data && typeof data.url === "string" ? data.url : "";
      const archivo = { nombre: storageNombre, tipo: fileType, url: urlPart };
      const nuevo = { ...bloquesContenido };
      if (!Array.isArray(nuevo[bloqueSeleccionado])) {
        nuevo[bloqueSeleccionado] = [];
      }
      const lista = [...(nuevo[bloqueSeleccionado] || [])];
      const nextLista = mergeArchivoIntoBloqueLista({
        tipoInsert,
        rutaDestinoKey,
        rutaPadreKey,
        subseccionTrim,
        posicion,
        lista,
        archivo,
      });
      if (!nextLista) {
        return setMensaje(docAddFailureMessage(tipoInsert));
      }
      nuevo[bloqueSeleccionado] = nextLista;
      setBloquesContenido(nuevo);
      setMensaje("✅ Documento agregado (pendiente de guardar).");
    } catch (e) {
      console.error("handleAddDoc", e);
      const detail = e instanceof Error ? e.message : "";
      setMensaje(detail ? `❌ Error subiendo archivo: ${detail}` : "❌ Error subiendo archivo.");
    }
  };

  const subirImagenSiHaceFalta = async (file) => {
    if (!file) return imagen;
    const base64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    const res = await fetch(`${API_URL}/subir-imagen-curso`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ nombre: file.name, tipo: file.type, contenidoBase64: base64 }),
    });

    if (!res.ok) {
      const text = await res.text();
      if (res.status === 403) {
        throw new Error("No tienes permiso para subir imágenes");
      }
      throw new Error(`Error subiendo imagen: ${res.status} ${text}`);
    }

    const data = await res.json();
    if (!data.url) throw new Error("Error subiendo imagen: No URL returned");
    return data.url;
  };

  const guardarHabilitado = canEditPrice || canManageTabs || canManageContent || canEditStatus;

  const canToggleCatalogoRole =
    COURSE_ADMIN_ASSIGN_ROLES.includes(rol) || puedePublicar === true;
  const canPublishCatalogo = canToggleCatalogoRole && puedePublicar;
  const catalogoPublicarDisabled =
    !canPublishCatalogo || catalogBusy || (publicadoAlumnos !== false && !cambiosPendientesPublicacion);
  const catalogoOcultarDisabled = !canPublishCatalogo || catalogBusy || publicadoAlumnos === false;

  const onPublicarCatalogo = useCallback(async () => {
    if (!id) return;
    const requestCourseId = id;
    setEstadoCatalogoMsg("");
    setCatalogBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/cursos/${requestCourseId}/publicar`, {
        method: "POST",
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(typeof data?.error === "string" && data.error.trim() ? data.error : r.statusText);
      }
      if (currentCatalogCourseIdRef.current !== requestCourseId) return;
      setPublicadoAlumnos(true);
      setCambiosPendientesPublicacion(false);
      setEstadoCatalogoMsg("Curso publicado en el catálogo.");
    } catch (e) {
      if (currentCatalogCourseIdRef.current !== requestCourseId) return;
      const msg = e instanceof Error ? e.message : "No se pudo publicar";
      setEstadoCatalogoMsg(`❌ ${msg}`);
    } finally {
      if (currentCatalogCourseIdRef.current === requestCourseId) {
        setCatalogBusy(false);
      }
    }
  }, [id]);

  const onDespublicarCatalogo = useCallback(async () => {
    if (!id) return;
    const requestCourseId = id;
    setEstadoCatalogoMsg("");
    setCatalogBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/cursos/${requestCourseId}/despublicar-catalogo`, {
        method: "POST",
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(typeof data?.error === "string" && data.error.trim() ? data.error : r.statusText);
      }
      if (currentCatalogCourseIdRef.current !== requestCourseId) return;
      setPublicadoAlumnos(false);
      setEstadoCatalogoMsg("Curso oculto del catálogo.");
    } catch (e) {
      if (currentCatalogCourseIdRef.current !== requestCourseId) return;
      const msg = e instanceof Error ? e.message : "No se pudo ocultar";
      setEstadoCatalogoMsg(`❌ ${msg}`);
    } finally {
      if (currentCatalogCourseIdRef.current === requestCourseId) {
        setCatalogBusy(false);
      }
    }
  }, [id]);

  const guardarCambios = async () => {
    try {
      setMensaje("");

      if (canEditStatus) {
        const closeDateError = getCourseCloseDateError(fechaCierre);
        if (closeDateError) {
          setMensaje(`⚠️ ${closeDateError}`);
          setActiveTab("datos");
          return;
        }
      }

      const tabsSaveBlocker = getCourseTabsSaveBlocker(pestanasCurso);
      if (tabsSaveBlocker === "unnamed") {
        setMensaje(`⚠️ ${getMessage("courseEditor.unnamedTabsSaveWarning")}`);
        setActiveTab("datos");
        return;
      }
      if (tabsSaveBlocker === "duplicate") {
        setMensaje(`⚠️ ${getMessage("courseEditor.duplicateTabsSaveWarning")}`);
        setActiveTab("datos");
        return;
      }

      let body;
      const urlImagen = archivoImagen ? await subirImagenSiHaceFalta(archivoImagen) : imagen;
      const administradoresPayload = canAssignCourseAdmins
        ? {
            administradores: resolveAdministradoresForSave(
              adminIds,
              usuariosStaff,
              usuariosStaffLoaded,
              adminLabelsById,
            ),
          }
        : {};

      const previousTabs = previousTabsFromCourseBaseline(courseCompareBaselineRef.current);
      const pestanasForSave = getNamedCourseTabs(pestanasCurso);
      const renameMap = buildTabRenameMap(previousTabs, pestanasForSave);
      const testsToSend = buildCourseTestsPayloadObject(
        applyTabRenameMapToTestsArray(tests, renameMap),
      );
      const contenidoPayload = buildContenidoPayloadForSave(
        bloquesContenido,
        pestanasForSave,
        previousTabs,
      );

      if (rol === "instructor") {
        // Mismo alcance que el backend: metadatos + contenido + tests; sin precio ni estado de publicación
        body = {
          titulo,
          descripcion,
          duracion: normalizeCourseDurationInput(duracion),
          imagen: urlImagen,
          tests: testsToSend,
          contenido: contenidoPayload,
          pestanasCurso: pestanasForSave,
          ...administradoresPayload,
        };
      } else {
        body = {
          titulo,
          descripcion,
          duracion: normalizeCourseDurationInput(duracion),
          precio: precio === "" ? null : Number(precio),
          descuento: descuento === "" ? null : Number(descuento),
          codigoDescuento,
          precioFinal: precioFinal === "" ? null : Number(precioFinal),
          imagen: urlImagen,
          esAbierto,
          ...(canEditStatus
            ? { fechaCierre: fechaCierre.trim() === "" ? null : fechaCierre }
            : {}),
          contenidoDescargable,
          tags: parseCourseTagsInput(tagsInput),
          ...(canEditStatus ? { disponibleCompra } : {}),
          tests: testsToSend,
          contenido: contenidoPayload,
          pestanasCurso: pestanasForSave,
          ...administradoresPayload,
        };
      }

      const snapshotNow = buildCourseEditSnapshotForSave({
        titulo,
        descripcion,
        duracion,
        imagen: urlImagen,
        precio,
        descuento,
        codigoDescuento,
        precioFinal,
        esAbierto,
        fechaCierre,
        contenidoDescargable,
        pestanasCurso,
        tests,
        bloquesContenido,
        adminIds,
        tagsInput,
        disponibleCompra,
        rol,
        adminIdKeyFn: adminIdKey,
      });
      const touchedFields = listCamposTocadosChanged(courseCompareBaselineRef.current, snapshotNow);
      if (touchedFields !== null && touchedFields.length > 0) {
        body.camposTocados = touchedFields;
      }

      const r = await fetch(`${API_URL}/api/admin/cursos/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!r.ok) {
        const text = await r.text();
        throw new Error(`Error guardando: ${r.status} ${text}`);
      }
      const data = await r.json();

      const nextImagen =
        data.curso && typeof data.curso === "object" && typeof data.curso.imagen === "string"
          ? data.curso.imagen
          : urlImagen;

      if (data.curso && typeof data.curso === "object") {
        const c = data.curso;
        if ("publicadoAlumnos" in c) {
          setPublicadoAlumnos(c.publicadoAlumnos !== false);
        }
        if ("disponibleCompra" in c) {
          setDisponibleCompra(c.disponibleCompra !== false);
        }
        if (Array.isArray(c.tags)) {
          setTagsInput(c.tags.join(", "));
        }
        if ("cambiosPendientesPublicacion" in c) {
          setCambiosPendientesPublicacion(c.cambiosPendientesPublicacion === true);
        }
        if (typeof c.imagen === "string" && c.imagen.trim() !== "") {
          setImagen(c.imagen);
        }
      }

      refreshCourseCompareBaseline({ nextImagen });
      setArchivoImagen(null);

      setMensaje(formatCourseEditSaveFeedback(data));
    } catch (e) {
      console.error(e);
      setMensaje("❌ " + e.message);
    }
  };

  const uploadBusy = uploadStatus === "reading" || uploadStatus === "uploading";
  const guardarDisabled = guardarHabilitado === false || uploadBusy;

  if (cargando) return <div>Cargando…</div>;

  const applyTabCount = (cantidad) => {
    const actuales = [...pestanasCurso];
    let nuevas = [...actuales];
    let removed = [];
    if (nuevas.length < cantidad) {
      while (nuevas.length < cantidad) nuevas.push("");
    } else if (nuevas.length > cantidad) {
      removed = nuevas.slice(cantidad);
      nuevas = nuevas.slice(0, cantidad);
    }
    setPestanasCurso(nuevas);
    lastUniqueTabKeysRef.current = resizeUniqueTabKeys(lastUniqueTabKeysRef.current, nuevas);
    let ids = [...tabStableIds];
    if (nuevas.length > ids.length) {
      while (ids.length < nuevas.length) ids.push(crypto.randomUUID());
    } else if (nuevas.length < ids.length) {
      ids = ids.slice(0, nuevas.length);
    }
    setTabStableIds(ids);
    if (removed.length > 0) {
      const nuevoContenido = { ...bloquesContenido };
      removed.forEach((tab) => {
        if (tab in nuevoContenido) {
          delete nuevoContenido[tab];
        }
      });
      setBloquesContenido(nuevoContenido);
      const testsActualizados = (tests || []).map((t) => ({
        ...t,
        pestanas: Array.isArray(t.pestanas) ? t.pestanas.filter((p) => !removed.includes(p)) : [],
      }));
      setTests(testsActualizados);
      if (removed.includes(bloqueSeleccionado)) {
        setBloqueSeleccionado("");
      }
    }
  };

  const preguntaActual = preguntas[preguntaSeleccionada] ?? preguntas[0];

  return (
    <div className="cc-container">
      <div className={`cc-main-container${actionBarVisible ? "" : " cc-main-container--panel-hidden"}`}>
        {actionBarVisible && (
          <ResponsiveActionSidebar
            id="course-actions-sidebar"
            className="cc-sidebar"
            openClassName="cc-sidebar--open"
            ariaLabel="Acciones de edición de curso"
          >
            <div className="cc-sidebar-header">
              <h4>Acciones</h4>
              <button
                type="button"
                className="cc-action-bar-toggle cc-action-bar-toggle--hide"
                onClick={() => setActionBarVisible(false)}
                title="Ocultar menú lateral"
                aria-label="Ocultar menú lateral"
              >
                <span className="cc-action-bar-toggle__icon" aria-hidden="true">
                  &#9664;
                </span>
                <span className="cc-action-bar-toggle__label">Ocultar menú</span>
              </button>
            </div>
            <ul className="cc-sidebar-links">
              {editCourseSidebarTabs.map(({ key, label }) => {
                const tabDisabled =
                  (key === "tests" && canManageTests === false) ||
                  (key === "docs" && canManageContent === false) ||
                  (key === "descuentos" && canEditPrice === false);
                return (
                  <li key={key}>
                    <button
                      type="button"
                      className={`cc-sidebar-button ${activeTab === key ? "active" : ""}`}
                      onClick={() => handleSidebarTabChange(key)}
                      disabled={tabDisabled}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
              <li>
                <button
                  type="button"
                  className="cc-sidebar-button staff-sidebar-exit-button staff-sidebar-exit-button--discard"
                  onClick={() => requestCourseEditorAction(() => navigate("/GestionCursos"))}
                >
                  {getMessage("courseEditor.cancelAndExit")}
                </button>
              </li>
              <li>
                <button className="cc-sidebar-button guardar-button" onClick={guardarCambios} disabled={guardarDisabled}>
                  Guardar cambios
                </button>
              </li>
            </ul>
          </ResponsiveActionSidebar>
        )}

        <main className="cc-content">
          {!actionBarVisible && (
            <button
              type="button"
              className="cc-action-bar-toggle cc-action-bar-toggle--show"
              onClick={() => setActionBarVisible(true)}
              title="Mostrar menú lateral de acciones"
              aria-label="Mostrar menú lateral"
            >
              <span className="cc-action-bar-toggle__icon" aria-hidden="true">
                &#9654;
              </span>
              <span className="cc-action-bar-toggle__label">Mostrar menú</span>
            </button>
          )}
          {mensaje && (
            <p ref={mensajeRef} className="cc-message" role="status" aria-live="polite" style={{ whiteSpace: "pre-line" }}>
              {mensaje}
            </p>
          )}
          {activeTab === "datos" && (
    <>
      {rol === "instructor" && (
        <p className="cc-message" style={{ backgroundColor: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9", padding: "10px 14px", borderRadius: 6, marginBottom: 16 }}>
          Como instructor puedes editar el contenido del curso, tests y pestañas. Precio, descuentos y curso abierto/cerrado los gestiona la dirección.
        </p>
      )}
      <h1>Editar Curso — Datos principales</h1>

      <div className="form-group cc-curso-datos-principales-row">
        <div className="cc-curso-titulo-field">
          <label htmlFor="editar-curso-titulo">Título del curso:</label>
          <input
            id="editar-curso-titulo"
            type="text"
            className="form-control cc-curso-titulo-input"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
          />
        </div>

        {rol !== "instructor" ? (
        <div style={{ display: "flex", flexDirection: "column", minWidth: 220 }}>
          <label htmlFor="editar-curso-tags">Categorías (etiquetas del catálogo):</label>
          <input
            id="editar-curso-tags"
            className="form-control medio-ancho4"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="Ej.: Oposiciones, Informática"
          />
          <span className="cc-field-help" style={{ fontSize: "0.85rem", marginTop: 4 }}>
            Separa con comas. Se usan en el filtro público de cursos.
          </span>
        </div>
        ) : null}

        <div className="form-group cc-curso-duracion-field">
          <label htmlFor="editar-curso-duracion">Duración:</label>
          <input
            id="editar-curso-duracion"
            type="text"
            className="form-control medio-ancho4"
            value={duracion}
            onChange={(e) => setDuracion(e.target.value)}
            placeholder="Ej.: 10 semanas (texto libre)"
          />
        </div>

        <div style={{ display:"flex", flexDirection:"column" }}>
          <label>Imagen del curso:</label>
          <small className="course-image-upload-help">
            Proporción recomendada 16:9 (por ejemplo, 1600 × 900 px). La imagen se mostrará completa sin deformarse.
          </small>
          <input type="file" accept="image/*" onChange={e=>setArchivoImagen(e.target.files[0])} />
          {(previewUrl || imagen) && (
            <div className="imagen-preview-container">
              <img
                src={previewUrl || imagen}
                alt="Previsualización"
                className="imagen-preview"
              />
            </div>
          )}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="editar-curso-descripcion">Descripción:</label>
        <CourseDescriptionEditor
          id="editar-curso-descripcion"
          className="cc-curso-descripcion-input"
          value={descripcion}
          onChange={setDescripcion}
          placeholder="Describe el curso con párrafos, listas y títulos básicos"
        />
      </div>

      {canAssignCourseAdmins && (
        <div className="form-group cc-course-admins-panel">
          <h3 className="cc-course-admins-title">Administradores del curso</h3>
          <p className="cc-course-admins-intro">
            Añade instructores (u otros usuarios del equipo) que podrán editar este curso, tests y contenido.
            Los alumnos no pueden ser administradores.
          </p>
          <ul className="cc-course-admins-list">
            {adminIds.length === 0 && (
              <li className="cc-course-admins-empty">Ningún administrador asignado (el curso no aparecerá en la gestión de instructores).</li>
            )}
            {adminIds.map((uid) => {
              const key = adminIdKey(uid);
              const u = usuariosStaff.find((x) => adminIdKey(x._id) === key);
              const snap = adminLabelsById[key];
              const label = formatAdminListLabel(snap || null, u || null, key);
              return (
                <li key={key} className="cc-course-admins-row">
                  <span className="cc-course-admins-label">{label}</span>
                  <button
                    type="button"
                    className="cc-course-admins-remove"
                    onClick={() => setAdminIds((prev) => prev.filter((x) => adminIdKey(x) !== key))}
                  >
                    Quitar
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="cc-course-admins-toolbar">
            <ViewportSelect
              className="form-control cc-course-admins-select"
              value={addAdminId}
              onChange={(e) => setAddAdminId(e.target.value)}
            >
              <option value="">Añadir del equipo (instructores, dirección…)</option>
              {usuariosStaff
                .filter((u) => !isAlumno(u.rol))
                .filter((u) => !adminIds.includes(adminIdKey(u._id)))
                .map((u) => (
                  <option key={adminIdKey(u._id)} value={adminIdKey(u._id)}>
                    {u.nombre} {u.primerApellido || ""} — {u.rol} ({u.email})
                  </option>
                ))}
            </ViewportSelect>
            <button
              type="button"
              className="cc-course-admins-toolbar-btn"
              disabled={!addAdminId}
              onClick={() => {
                if (!addAdminId) return;
                const key = adminIdKey(addAdminId);
                const picked = usuariosStaff.find((x) => adminIdKey(x._id) === key);
                const snap = picked ? snapshotFromPopulatedAdmin(picked) : null;
                if (snap) {
                  setAdminLabelsById((prev) => ({ ...prev, [key]: snap }));
                }
                setAdminIds((prev) => (prev.includes(key) ? prev : [...prev, key]));
                setAddAdminId("");
              }}
            >
              Añadir
            </button>
            <button
              type="button"
              className="cc-course-admins-toolbar-btn cc-course-admins-toolbar-btn--primary"
              onClick={guardarAdministradoresCurso}
              disabled={savingAdmins}
            >
              {savingAdmins ? "Guardando…" : "Guardar administradores"}
            </button>
          </div>
        </div>
      )}

      <section className="form-group cc-price-discount-panel" aria-labelledby="editar-curso-price-discount-title">
        <div className="cc-price-discount-panel__header">
          <h3 id="editar-curso-price-discount-title">Precio y descuento del curso</h3>
          <p>
            Configura únicamente los datos comerciales de este curso: precio base, descuento promocional, código y precio final calculado.
          </p>
        </div>
        <div className="cc-price-discount-grid">
        <div>
          <label htmlFor="editar-curso-precio">Precio:</label>
          <input id="editar-curso-precio" type="number" className="form-control medio-ancho1" onWheel={e=>e.target.blur()}
                  value={precio} onChange={e=>setPrecio(e.target.value)} disabled={!canEditPrice} title={!canEditPrice ? "Requiere permiso para editar precio y descuentos" : undefined} />
        </div>
        <div>
          <label htmlFor="editar-curso-descuento">Descuento:</label>
          <input id="editar-curso-descuento" type="number" className="form-control medio-ancho1" onWheel={e=>e.target.blur()}
                  value={descuento} onChange={e=>setDescuento(e.target.value)} disabled={!canEditPrice} title={!canEditPrice ? "Requiere permiso para editar precio y descuentos" : undefined} />
        </div>
        <div>
          <label htmlFor="editar-curso-codigo-descuento">Código:</label>
          <input id="editar-curso-codigo-descuento" className="form-control medio-ancho1" value={codigoDescuento} onChange={e=>setCodigoDescuento(e.target.value)} disabled={!canEditPrice} title={!canEditPrice ? "Requiere permiso para editar precio y descuentos" : undefined} />
        </div>
        <div>
          <label htmlFor="editar-curso-precio-final">Precio final:</label>
          <input id="editar-curso-precio-final" className="form-control medio-ancho1" value={precioFinal} readOnly />
        </div>
        </div>
      </section>

      {/* Switch Curso Abierto/Cerrado */}
      <div className="form-group cc-course-status-control" style={{ marginTop: 20, padding: 15, border: '1px solid #e0e0e0', borderRadius: 8, backgroundColor: '#f9f9f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <label style={{ display: 'flex', alignItems: 'center', cursor: canEditStatus ? 'pointer' : 'not-allowed', gap: 15 }}>
            <div style={{ position: 'relative', width: 50, height: 26 }}>
              <input 
                type="checkbox" 
                checked={esAbierto} 
                onChange={(e) => {
                  if (canEditStatus) {
                    setEsAbierto(e.target.checked);
                    setMensaje(`⚠️ Estado cambiado a ${e.target.checked ? "ABIERTO" : "CERRADO"}. Pulsa "Guardar cambios" para confirmar.`);
                  }
                }} 
                disabled={!canEditStatus}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: esAbierto ? '#4caf50' : '#ccc',
                transition: '.4s', borderRadius: 34
              }}>
                <span style={{
                  position: 'absolute', content: '""', height: 20, width: 20, left: 3, bottom: 3,
                  backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                  transform: esAbierto ? 'translateX(24px)' : 'translateX(0)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}></span>
              </span>
            </div>
            <div>
              <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: esAbierto ? '#2e7d32' : '#616161' }}>
                {esAbierto ? "CURSO ABIERTO" : "CURSO CERRADO"}
              </span>
              <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                {esAbierto 
                  ? "Los alumnos pueden acceder a los tests sin completar el temario previo." 
                  : "Los alumnos deben completar el temario antes de acceder a los tests (Comportamiento por defecto)."}
              </p>
            </div>
          </label>
        </div>
        {!canEditStatus && (
          <span style={{ fontSize: '0.8rem', color: '#d32f2f', backgroundColor: '#ffebee', padding: '4px 8px', borderRadius: 4 }}>
            Requiere permiso de Admin/Director
          </span>
        )}
      </div>

      {canToggleCatalogoRole && catalogStateLoaded && (
        <div
          className="form-group cc-catalog-panel"
          style={{
            marginTop: 20,
            padding: 15,
            border: "1px solid #e0e0e0",
            borderRadius: 8,
            backgroundColor: "#f9f9f9",
          }}
        >
          <h5 className="cc-catalog-panel__title">Catálogo (registro y compra)</h5>
          {cambiosPendientesPublicacion && (
            <div className="cc-catalog-panel__pending-badge">
              ⚠️ Cambios pendientes de publicación
            </div>
          )}
          <p className="cc-catalog-panel__description">
            {publicadoAlumnos === false
              ? "El curso está oculto del catálogo público."
              : cambiosPendientesPublicacion
                ? "Los cambios guardados están listos para publicarse en el catálogo."
                : "Visible en catálogo; los alumnos inscritos siguen según reglas de acceso del curso."}
          </p>
          {!puedePublicar && (
            <p className="cc-catalog-panel__warning">
              Tu usuario no tiene el permiso <strong>puedePublicar</strong>. Pide a dirección que lo active para poder publicar estos cambios.
            </p>
          )}
          {estadoCatalogoMsg && <p className="cc-message">{estadoCatalogoMsg}</p>}
          <div className="cc-catalog-panel__actions">
            <button
              type="button"
              className="cc-sidebar-button"
              disabled={catalogoPublicarDisabled}
              onClick={onPublicarCatalogo}
              title={!canPublishCatalogo ? "Requiere permiso puedePublicar" : undefined}
            >
              Publicar en catálogo
            </button>
            <button
              type="button"
              className="cc-sidebar-button"
              disabled={catalogoOcultarDisabled}
              onClick={onDespublicarCatalogo}
              title={!canPublishCatalogo ? "Requiere permiso puedePublicar" : undefined}
            >
              Ocultar del catálogo
            </button>
          </div>
          {canEditStatus ? (
            <label className="cc-catalog-panel__checkbox">
              <input
                type="checkbox"
                checked={disponibleCompra}
                onChange={(e) => setDisponibleCompra(e.target.checked)}
              />
              <span>Permitir compra e inscripción desde el catálogo (si se desmarca, el curso aparece como «Próximamente»)</span>
            </label>
          ) : null}
        </div>
      )}

      <div className="form-group" style={{ marginTop: 16 }}>
        <label htmlFor="editar-fecha-cierre">Fecha de cierre del curso (opcional)</label>
        <p className="cc-field-help" style={{ fontSize: "0.85rem", margin: "4px 0 8px" }}>
          Tras esta fecha el alumno no accede al contenido; puedes cambiarla o borrarla después. Los instructores no la
          editan aquí (lo gestiona dirección).
        </p>
        <input
          id="editar-fecha-cierre"
          type="date"
          className="form-control medio-ancho4"
          value={fechaCierre}
          onChange={(e) => setFechaCierre(e.target.value)}
          min={COURSE_CLOSE_DATE_MIN}
          max={COURSE_CLOSE_DATE_MAX}
          disabled={!canEditStatus}
        />
      </div>

      <div className="form-group" style={{ marginTop: 16 }}>
        <label>
          <h5>Número de pestañas del curso:</h5>
        </label>
        <CourseTabCountInput
          tabCount={pestanasCurso.length}
          tabs={pestanasCurso}
          bloquesContenido={bloquesContenido}
          tests={tests}
          disabled={!canManageTabs}
          title={!canManageTabs ? "Requiere permiso para gestionar pestañas" : undefined}
          onCommit={applyTabCount}
        />
      </div>

      <div className="nombre-pestanas-grid-scroll">
        {pestanasCurso.map((nombre, index) => (
          <div key={index} className="form-group">
            <label>Pestaña {index + 1}:</label>
            <input
              className="form-control nombre-pestana-input"
              placeholder="Nombre de la pestaña"
              type="text"
              value={nombre}
              disabled={!canManageTabs}
              title={!canManageTabs ? "Requiere permiso para gestionar pestañas" : undefined}
              onChange={(e) => {
                  const result = applyCourseTabNameInput(
                    pestanasCurso,
                    index,
                    e.target.value ?? "",
                    lastUniqueTabKeysRef.current,
                  );
                  lastUniqueTabKeysRef.current = result.uniqueKeys;
                  const { tabs: nuevas, oldRaw, remapFrom, remapTo, shouldRemap } = result;
                  setPestanasCurso(nuevas);
                  if (shouldRemap) {
                    const nuevoContenido = { ...bloquesContenido };
                    const sourceKey = findTemarioContentKey(nuevoContenido, [remapFrom, oldRaw]);
                    if (sourceKey && sourceKey !== remapTo) {
                      nuevoContenido[remapTo] = nuevoContenido[sourceKey];
                      delete nuevoContenido[sourceKey];
                      setBloquesContenido(nuevoContenido);
                    }
                    const testsActualizados = (tests || []).map(t => ({
                      ...t,
                      pestanas: Array.isArray(t.pestanas) ? t.pestanas.map(p=> {
                        const pk = normalizeCourseTabName(p);
                        return pk === remapFrom || p === oldRaw ? remapTo : p;
                      }) : []
                    }));
                    setTests(testsActualizados);
                    if (bloqueSeleccionado === oldRaw || (bloqueSeleccionado ?? "").trim() === remapFrom) {
                      setBloqueSeleccionado(remapTo);
                    }
                  }
                }}
              onBlur={() => {
                if (hasDuplicateCourseTabs(pestanasCurso)) {
                  setMensaje(`⚠️ ${getMessage("courseEditor.duplicateTabsSaveWarning")}`);
                }
              }}
            />
          </div>
        ))}
      </div>

    </>
  )}

          {activeTab === "tests" && (
            <EditarCursoTestsTab
              courseId={id}
              uploadStatus={uploadStatus}
              pestanasCurso={pestanasCurso}
              canManageTests={canManageTests}
              testActual={testActual}
              setTestActual={setTestActual}
              preguntas={preguntas}
              setPreguntas={setPreguntas}
              preguntaSeleccionada={preguntaSeleccionada}
              setPreguntaSeleccionada={setPreguntaSeleccionada}
              preguntaActual={preguntaActual}
              modoEdicion={modoEdicion}
              setModoEdicion={setModoEdicion}
              indexEdicion={indexEdicion}
              setIndexEdicion={setIndexEdicion}
              tests={tests}
              setTests={setTests}
              setMensaje={setMensaje}
              testsPorBloque={testsPorBloque}
              bloquesContenido={bloquesContenido}
              setBloquesContenido={setBloquesContenido}
            />
          )}

          {activeTab === "docs" && (
            <EditarCursoDocsTab
              rol={rol}
              rolReady={rolReady}
              archivoSeleccionado={archivoSeleccionado}
              contenidoDescargable={contenidoDescargable}
              setContenidoDescargable={setContenidoDescargable}
              uploadStatus={uploadStatus}
              uploadProgress={uploadProgress}
              lastUploadError={lastUploadError}
              setArchivoSeleccionado={setArchivoSeleccionado}
              setUploadProgress={setUploadProgress}
              setUploadStatus={setUploadStatus}
              setLastUploadError={setLastUploadError}
              setMensaje={setMensaje}
              canManageContent={canManageContent}
              bloqueSeleccionado={bloqueSeleccionado}
              setBloqueSeleccionado={setBloqueSeleccionado}
              pestanasCurso={pestanasCurso}
              tipoInsert={tipoInsert}
              setTipoInsert={setTipoInsert}
              rutaPadreKey={rutaPadreKey}
              setRutaPadreKey={setRutaPadreKey}
              rutaDestinoKey={rutaDestinoKey}
              setRutaDestinoKey={setRutaDestinoKey}
              subseccion={subseccion}
              setSubseccion={setSubseccion}
              posicion={posicion}
              setPosicion={setPosicion}
              bloquesContenido={bloquesContenido}
              setBloquesContenido={setBloquesContenido}
              handleAddDoc={handleAddDoc}
              lastAttemptedFileRef={lastAttemptedFileRef}
              courseTests={tests}
              setTests={setTests}
            />
          )}

          {activeTab === "vista" && (
            <EditarCursoVistaTab
              courseId={id}
              titulo={titulo}
              pestanasCurso={pestanasCurso}
              bloquesContenido={bloquesContenido}
              tests={tests}
              onBeforeStudentPreview={confirmLeaveCourseEditor}
            />
          )}

          {activeTab === "descuentos" && (
            <EditarCursoDescuentosTab
              codigoDescuento={codigoDescuento}
              descuento={descuento}
              precio={precio}
              precioFinal={precioFinal}
            />
          )}
          {activeTab === "testimonios" && (
            <EditarCursoTestimoniosTab courseId={id} setMensaje={setMensaje} />
          )}
        </main>
      </div>
    </div>
  );
};

export default EditarCurso;
